/**
 * Complete your kit (sections/matching-set.liquid): each card is a native /cart/add form (no-JS fallback).
 * With JS, its one chosen variant goes in one /cart/add.js request. Never retries. After a failure the cart
 * is re-read and the shopper is told only what that cart proves; a transport failure (no answer or 5xx) may
 * still land later, so it locks that card's button rather than inviting a repeat.
 * The buttons deliberately have no name="add": Selleasy hijacks every [name="add"] click for its popup.
 */

/**
 * @param {Array<{id: number, quantity: number, title: string}>} items
 * @param {object} opts - { fetchFn, cartUrl, addUrl, sections, sectionsUrl }
 * @returns {Promise<{status: 'not-sent'|'added'|'partial'|'failed'|'unknown', added: Array, notAdded: Array,
 *   before?: object, after?: object|null, data?: object, message?: string}>}
 *   'failed'/'partial' only follow a definitive 4xx; 'unknown' means the cart can't prove the outcome.
 */
async function addSetToCart(items, opts) {
  const fetchFn = opts.fetchFn || ((...args) => window.fetch(...args));
  const getCart = async () => {
    const res = await fetchFn(`${opts.cartUrl}.js`, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(res.status);
    return res.json();
  };
  const readCart = () => getCart().catch(() => null);

  let before;
  try {
    before = await getCart();
  } catch (error) {
    return { status: 'not-sent', added: [], notAdded: items };
  }

  let res = null;
  let data = {};
  try {
    res = await fetchFn(`${opts.addUrl}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: items.map(({ id, quantity }) => ({ id, quantity })),
        sections: opts.sections,
        sections_url: opts.sectionsUrl
      })
    });
    data = (await res.json().catch(() => null)) || {};
  } catch (error) {
    // Transport failure: the add may still be processed after this point.
  }

  if (res && res.ok) return { status: 'added', added: items, notAdded: [], data, before, after: await readCart() };

  const definitive = Boolean(res) && res.status >= 400 && res.status < 500;
  const message = typeof data.description === 'string' ? data.description : data.message;
  const after = await readCart();
  const was = cartQuantity(before);
  const now = cartQuantity(after);
  const added = after ? items.filter((item) => now(item.id) > was(item.id)) : [];
  const notAdded = items.filter((item) => !added.includes(item));
  const result = { added, notAdded, before, after, message };
  if (!notAdded.length) return { status: 'added', ...result };
  if (!definitive || !after) return { status: 'unknown', ...result };
  return { status: added.length ? 'partial' : 'failed', ...result };
}

/** @returns {function(number): number} Quantity of a variant in a /cart.js payload (0 if no cart). */
function cartQuantity(cart) {
  return (id) => (cart ? cart.items : [])
    .filter((line) => line.variant_id === id)
    .reduce((sum, line) => sum + line.quantity, 0);
}

if (typeof module !== 'undefined') module.exports = { addSetToCart };

if (typeof customElements !== 'undefined' && !customElements.get('matching-set')) {
  class MatchingSet extends HTMLElement {
    connectedCallback() {
      if (this.bound) return; // Re-inserted: listeners are already bound.
      this.bound = true;
      // Native required-select validation runs before this; the browser focuses the empty size and says so.
      this.addEventListener('submit', (evt) => {
        evt.preventDefault();
        this.submit(evt.target);
      });
    }

    async submit(form) {
      if (form.busy || form.locked) return;
      const input = form.elements.id;
      const error = form.querySelector('.js-set-error');
      const title = form.closest('.matching-set__piece').dataset.title;
      // A sold-out option can still end up selected (autofill, bfcache restore); never treat it as a choice.
      const option = input.selectedOptions ? input.selectedOptions[0] : null;
      if (!input.value || (option && option.disabled)) {
        MatchingSet.showError(error, `Choose a size for: ${title}.`);
        input.focus();
        return;
      }
      MatchingSet.showError(error, '');
      this.setBusy(form, true);
      try {
        await this.add(form, error, { id: Number(input.value), quantity: 1, title });
      } finally {
        this.setBusy(form, false);
      }
    }

    static showError(el, text, withCartLink) {
      el.hidden = !text;
      el.textContent = text || '';
      if (text && withCartLink) {
        const link = document.createElement('a');
        link.href = theme.routes.cart;
        link.textContent = ' View basket';
        el.append(link);
      }
    }

    setBusy(form, busy) {
      const button = form.querySelector('button[type="submit"]');
      form.busy = busy;
      form.setAttribute('aria-busy', busy);
      button.classList.toggle('is-loading', busy);
      if (busy || form.locked) {
        button.setAttribute('aria-disabled', 'true');
      } else {
        button.removeAttribute('aria-disabled');
      }
    }

    async add(form, error, item) {
      const drawer = document.querySelector('cart-drawer');
      const drawerSection = drawer && drawer.closest('.shopify-section');
      const sections = ['cart-icon-bubble'];
      if (drawerSection) sections.push(drawerSection.id.replace('shopify-section-', ''));
      const cartError = (theme.strings && theme.strings.cartError) || 'There was an error while updating your cart. Please try again.';

      const result = await addSetToCart([item], {
        cartUrl: theme.routes.cart,
        addUrl: theme.routes.cartAdd,
        sections: sections.join(','),
        sectionsUrl: window.location.pathname
      });

      if (result.status === 'not-sent') {
        MatchingSet.showError(error, cartError);
        return;
      }

      if (result.status === 'added') {
        // Same events as the theme's product-form.
        const was = cartQuantity(result.before)(item.id);
        const detail = { cart: result.after, variantId: item.id };
        if (was) Object.assign(detail, { oldQuantity: was, newQuantity: result.after ? cartQuantity(result.after)(item.id) : was + 1 });
        this.dispatchEvent(new CustomEvent(was ? 'on:line-item:change' : 'on:cart:add', { bubbles: true, detail }));

        const html = result.data && result.data.sections;
        const drawerReady = drawerSection && html && sections.every((id) => typeof html[id] === 'string');
        if (drawerReady && !['page', 'no-js'].includes(theme.settings.afterAtc)) {
          try {
            await drawer.renderContents(result.data, theme.settings.afterAtc === 'drawer');
            return;
          } catch (err) {
            // Fall back to the cart page, which always shows the real basket.
          }
        }
        window.location.href = theme.routes.cart;
        return;
      }

      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
      if (result.status === 'failed') {
        MatchingSet.showError(error, `Nothing was added. ${result.message || cartError}`);
      } else {
        form.locked = true;
        MatchingSet.showError(error, `We couldn't confirm ${item.title}: it may still reach your basket. Check your basket before adding again.`, true);
      }
    }
  }

  customElements.define('matching-set', MatchingSet);
}
